// VO2max (#194, #202) with every source it comes from, and your heart-rate
// zones as bpm ranges (shown in the Heart rate & zones sheet).

import React, { useEffect, useState } from "react";
import { Bike, Sailboat, X } from "lucide-react";
import { cn } from "../../lib/utils";
import DetailSheet from "../session/generate/DetailSheet";
import api from "../../services/api";
import { ZONE_COLOURS } from "../../utils/heartRateZones";
import { currentVo2max } from "../../utils/vo2max";
import { latest2k, vo2maxFrom2k } from "../../utils/rowing";
import { calibrateAirBike } from "../../utils/airCalibration";
import { clock } from "../../utils/benchmarkNormalise";
import { Field } from "./ProfileParts";

const ZONE_NAMES = ["Zone 1 · very light", "Zone 2 · easy", "Zone 3 · moderate", "Zone 4 · hard", "Zone 5 · maximum"];

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

/**
 * VO2max history from the server ([] signed out or on error), and `remove`
 * to drop one session's estimate.
 */
export const useVo2maxHistory = (enabled) => {
  const [all, setAll] = useState([]);
  const remove = async (id) => {
    await api.clearVo2max(id);
    setAll((prev) => prev.filter((r) => r.id !== id));
  };
  // A ride saved as a bike estimate that was really on an air bike: keep its
  // steady effort for calibration instead (#202).
  const markAirBike = async (id) => {
    const updated = await api.markVo2maxAirBike(id);
    setAll((prev) => prev.map((r) => (r.id === id ? { ...r, vo2max: null, air_steady: updated.air_steady } : r)));
  };
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    api
      .getVo2maxHistory()
      .then((r) => live && Array.isArray(r) && setAll(r))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [enabled]);
  return {
    rows: all.filter((x) => x?.vo2max?.value),
    airRows: all.filter((x) => x?.air_steady?.watts),
    remove,
    markAirBike,
  };
};

/**
 * Your VO2max from the best source there is (#194, #202): steady bike rides,
 * the air bike calibrated to your 2k, or the 2k itself (Concept2). Shared by
 * Profile and the Progress page (#203); spread `sheet` onto Vo2maxSheet.
 */
export const useVo2max = ({ signedIn, userProfile, benchmarks = [], sex = null }) => {
  const { rows, airRows, remove, markAirBike } = useVo2maxHistory(signedIn);
  const bike = currentVo2max(rows);
  const twoK = latest2k(benchmarks);
  const trained = (userProfile?.trainingExperience || "beginner") !== "beginner";
  const rowingVo2 = twoK ? vo2maxFrom2k({ sec: twoK.sec, kg: userProfile?.bodyWeightKg, sex, trained }) : null;
  // Your air bike, calibrated to that 2k (utils/airCalibration).
  const rowing = rowingVo2 ? { value: rowingVo2.value, date: twoK.date } : null;
  const air = calibrateAirBike({ rows: airRows, anchor: rowing, kg: userProfile?.bodyWeightKg });
  return {
    bike,
    air,
    headline: bike?.value ?? air?.current?.value ?? rowingVo2?.value ?? null,
    // Worked out from heart rate, so an estimated max makes it approximate.
    fromHeartRate: !!(bike || air?.current),
    sheet: { rows, airRows, air, rowing, twoK, sex, onRemove: remove, onMarkAirBike: markAirBike },
  };
};

// A small trend line: one dot per estimate, oldest left.
const Trend = ({ rows }) => {
  if (rows.length < 2) return null;
  const vals = rows.map((r) => r.vo2max.value);
  const lo = Math.min(...vals) - 1;
  const hi = Math.max(...vals) + 1;
  const W = 300;
  const H = 80;
  const x = (i) => 8 + (i * (W - 16)) / (rows.length - 1);
  const y = (v) => H - 8 - ((v - lo) / (hi - lo)) * (H - 16);
  const d = vals.map((v, i) => `${i ? "L" : "M"}${x(i)},${y(v)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-20 w-full" role="img" aria-label={`VO2max from ${vals[0]} to ${vals[vals.length - 1]}`}>
      <path d={d} fill="none" stroke="hsl(var(--primary))" strokeWidth="2" />
      {vals.map((v, i) => (
        <circle key={rows[i].id} cx={x(i)} cy={y(v)} r="3" fill="hsl(var(--primary))" />
      ))}
    </svg>
  );
};

// One list of estimates, newest first, each removable with a second tap.
const EstimateList = ({ items, confirming, setConfirming, remove, markAirBike }) =>
  items.length > 0 && (
    <div className="overflow-hidden rounded-xl border border-border bg-card divide-y divide-border">
      {[...items].reverse().map((r) => (
        <div key={r.id} className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 py-1.5 pl-3 pr-1.5">
          <span className="min-w-0">
            <span className="block text-sm font-medium text-foreground">{fmtDate(r.started_at)}</span>
            <span className="block truncate text-xs text-muted-foreground">{r.detail}</span>
          </span>
          <span className="text-sm font-semibold tabular-nums">{r.value}</span>
          {confirming === r.id ? (
            <span className="flex gap-1">
              {markAirBike && r.bike && (
                <button
                  type="button"
                  onClick={() => markAirBike(r.id)}
                  className="h-8 rounded-md border border-border px-2 text-xs font-semibold text-foreground"
                >
                  Air bike
                </button>
              )}
              <button
                type="button"
                onClick={() => remove(r.id)}
                className="h-8 rounded-md bg-destructive px-2.5 text-xs font-semibold text-destructive-foreground"
              >
                Remove
              </button>
            </span>
          ) : (
            <button
              type="button"
              aria-label={`Remove the estimate from ${fmtDate(r.started_at)}`}
              onClick={() => setConfirming(r.id)}
              className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );

/**
 * Your VO2max: from steady bike rides with power, else from your air bike
 * calibrated to your 2k (utils/airCalibration), else from the 2k itself.
 * `rowing` is { value, date } from the Rowing sheet's 2k, or null.
 */
export const Vo2maxSheet = ({
  open,
  onClose,
  rows,
  airRows = [],
  air = null,
  rowing = null,
  twoK = null,
  sex = null,
  onSetSex,
  signedIn,
  onAddBenchmark,
  onRemove,
  onMarkAirBike,
  hasWeight,
  maxEstimated,
}) => {
  const bike = currentVo2max(rows);
  // The estimate a first tap on × picked; a second tap removes it.
  const [confirming, setConfirming] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) setConfirming(null);
    setError("");
  }, [open]);
  const remove = (id) =>
    onRemove(id)
      .then(() => setConfirming(null))
      .catch(() => setError("Couldn't remove it. Check your connection and try again."));
  const markAirBike = (id) =>
    onMarkAirBike(id)
      .then(() => setConfirming(null))
      .catch(() => setError("Couldn't change it. Check your connection and try again."));

  const headline = bike
    ? { value: bike.value, note: bike.count > 1 ? `middle of your last ${bike.count} rides` : "from one ride" }
    : air?.current
      ? {
          value: air.current.value,
          note: `air bike, calibrated to your 2k · ${air.current.count > 1 ? `middle of ${air.current.count} rides` : "one ride"}`,
        }
      : rowing
        ? { value: rowing.value, note: `from your 2k${rowing.date ? `, ${fmtDate(rowing.date)}` : ""}` }
        : null;
  const bikeItems = rows.map((r) => ({
    id: r.id,
    started_at: r.started_at,
    value: r.vo2max.value,
    detail: `${r.vo2max.watts} W at ${r.vo2max.hr} bpm · ${r.vo2max.minutes || 10} min steady`,
    bike: true,
  }));
  const airItems = (air?.estimates || []).map((e) => {
    const steady = airRows.find((r) => r.id === e.id)?.air_steady || {};
    return { ...e, detail: `Air bike · ${steady.watts} W at ${steady.hr} bpm · ${steady.minutes || 10} min steady` };
  });
  // Every source and what it gives, so a missing one says how to get it.
  const sources = [
    {
      key: "row",
      Icon: Sailboat,
      label: "Rowing 2k",
      value: rowing?.value,
      detail: !twoK
        ? "Log a 2k row in Benchmarks"
        : rowing
          ? `${clock(twoK.sec)}${twoK.date ? ` on ${fmtDate(twoK.date)}` : ""}${twoK.stale ? " · over six months old" : ""}`
          : !hasWeight
            ? `${clock(twoK.sec)} · add your body weight`
            : `${clock(twoK.sec)} · set your sex in Body & heart`,
      onClick: !twoK && signedIn ? onAddBenchmark : null,
    },
    {
      key: "air",
      Icon: Bike,
      label: "Air bike",
      value: air?.current?.value,
      detail: air
        ? `Calibrated to your 2k · ${air.estimates.length} ${air.estimates.length === 1 ? "ride" : "rides"}`
        : airRows.length
          ? `${airRows.length} steady ${airRows.length === 1 ? "ride" : "rides"} · needs a 2k within four weeks of one`
          : "No steady rides yet",
    },
    {
      key: "bike",
      Icon: Bike,
      label: "Bike with power",
      value: bike?.value,
      detail: bike ? `${rows.length} ${rows.length === 1 ? "ride" : "rides"}` : "No rides yet",
    },
  ];
  const listProps = { confirming, setConfirming, remove, markAirBike: onMarkAirBike ? markAirBike : null };

  return (
    <DetailSheet
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title="VO2max"
      description="How much oxygen you can use at your hardest, in ml per kg per minute. Higher is fitter."
      showDescription
    >
      {headline ? (
        <div className="grid gap-1 text-center">
          <span className="text-4xl font-bold tabular-nums text-foreground">{headline.value}</span>
          <span className="text-xs text-muted-foreground">{`ml/kg/min · ${headline.note}`}</span>
          {maxEstimated && (bike || air?.current) && (
            <span className="text-xs text-yellow-500">
              Rough: your max heart rate is estimated from your age. Set it in Heart rate &amp; zones.
            </span>
          )}
        </div>
      ) : (
        <p className="text-sm leading-relaxed text-muted-foreground">
          {hasWeight
            ? "No estimate yet. Any of the sources below gives you one."
            : "No estimate yet. Add your body weight in Body & heart first, then any of the sources below gives you one."}
        </p>
      )}
      <Field label="Where it comes from">
        <div className="overflow-hidden rounded-xl border border-border bg-card divide-y divide-border">
          {sources.map(({ key, Icon, label, value, detail, onClick }) => {
            const Row = onClick ? "button" : "div";
            return (
              <Row
                key={key}
                {...(onClick ? { type: "button", onClick } : {})}
                className="grid w-full grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-2.5 px-3 py-2.5 text-left"
              >
                <Icon className="h-[18px] w-[18px] text-muted-foreground" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-foreground">{label}</span>
                  <span className={cn("block truncate text-xs", onClick ? "text-primary" : "text-muted-foreground")}>
                    {detail}
                  </span>
                </span>
                <span className="text-sm font-semibold tabular-nums">{value ?? "–"}</span>
              </Row>
            );
          })}
        </div>
      </Field>
      {twoK && signedIn && (
        <p className="text-xs text-muted-foreground">
          {sex ? `Your 2k uses Concept2's formula for ${sex === "female" ? "women" : "men"}. ` : "Your 2k needs your sex: Concept2 has a formula for men and one for women. "}
          <button type="button" className="text-primary underline underline-offset-2" onClick={onSetSex}>
            {sex ? "Change" : "Set it"}
          </button>
        </p>
      )}
      <Trend rows={rows} />
      {bikeItems.length > 0 && <EstimateList items={bikeItems} {...listProps} />}
      {airItems.length > 0 && <EstimateList items={airItems} {...listProps} />}
      {error && <p className="text-xs text-destructive">{error}</p>}
      <p className="text-xs leading-relaxed text-muted-foreground">
        Your 2k: Concept2&rsquo;s estimate from your time and weight. Bike rides: every steady 10 minutes, from the oxygen your watts cost and how close your heart rate was to your
        max. Air bikes have no published equation, so Khronos calibrates yours: a steady ride near a 2k test sets what a
        watt costs you on it, and later rides use that. One ride is within about 10 to 15%; the big number is the middle
        of your last five from the past eight weeks.
      </p>
      {bikeItems.length > 0 && (
        <p className="text-xs leading-relaxed text-muted-foreground">
          An older ride that was on an air bike? Tap its × and choose <b className="font-semibold">Air bike</b> to use it
          for calibration instead.
        </p>
      )}
    </DetailSheet>
  );
};

/** Your five zones as bpm ranges, in the zone colours. */
export const ZoneTable = ({ floors }) => (
  <div className="overflow-hidden rounded-xl border border-border bg-card divide-y divide-border">
    {ZONE_NAMES.map((name, i) => (
      <div key={name} className="flex items-baseline justify-between gap-2 px-3 py-2.5">
        <span className="flex items-center gap-2 text-sm text-foreground">
          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full" style={{ background: ZONE_COLOURS[i + 1] }} />
          {name}
        </span>
        <span className="text-sm font-semibold tabular-nums">
          {i < 4 ? `${floors[i]}–${floors[i + 1] - 1} bpm` : `${floors[i]}+ bpm`}
        </span>
      </div>
    ))}
  </div>
);
